<template>
  <InputGroup class="clearable-input-text">
    <InputText
      :id="id"
      ref="inputText"
      v-model="internalValue"
      :disabled="!!disabled"
      class="w-full"
    />

    <InputGroupAddon v-if="internalValue">
      <Button
        :aria-label="t('common.clearParam', { param: label })"
        :disabled="!!disabled"
        icon="pi pi-times"
        tabindex="-1"
        @click="onClear"
      />
    </InputGroupAddon>
  </InputGroup>
</template>

<script lang="ts" setup>
import type { ComponentPublicInstance, Ref } from 'vue';
import { nextTick, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useModelValue } from '@rygen/rygen-ui/composables';

import Button from 'primevue/button';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import InputText from 'primevue/inputtext';

const props = defineProps({
  modelValue: {
    type: String,
    default: () => '',
  },
  id: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: () => '',
  },
  disabled: {
    // This lets consumers pass anything to `disabled`, and we'll !! it to
    // convert it to a boolean
    type: [Boolean, String, Number, Array, Object],
    default: () => false,
  },
});
const { modelValue, id, label, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: string): void;
}>();

const { internalValue } = useModelValue(modelValue as Ref<string | undefined>, {
  emit,
  factoryMethod: (newVal: string | undefined) => newVal,
});
const { t } = useI18n();

const inputText: Ref<ComponentPublicInstance | null> = ref(null);
const onClear = () => {
  internalValue.value = undefined;
  const input = inputText.value as ComponentPublicInstance;
  nextTick(() => input.$el.focus());
};
</script>
